import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Zap, Swords, ScanLine, Sparkles, Trophy, Wallet, HeartHandshake,
  Globe2, Calendar, Crown, ArrowRight, Play, ShieldCheck, TrendingUp,
  Radio, Users, MapPin,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import LanguageToggle from '../components/LanguageToggle';
import UserAvatar from '../components/UserAvatar';

// A simple animated counter
function useCountUp(target, duration = 1400) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target == null) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

const FEATURES = [
  {
    icon: Wallet, color: '#00F0FF',
    title: 'Billetera Aura',
    desc: 'Cada nuevo usuario recibe 10,000 puntos. Todo movimiento queda registrado en un ledger inmutable.',
    testId: 'feature-wallet',
  },
  {
    icon: Swords, color: '#FF2E8B',
    title: 'Batallas Aura en vivo',
    desc: 'Apuesta puntos, escanea el QR de tu contrincante y deja que la comunidad decida quién se lleva el pot.',
    testId: 'feature-battles',
  },
  {
    icon: HeartHandshake, color: '#FFD166',
    title: 'Puntos Sociales · 1M pool',
    desc: 'Recompensa buenas acciones sin quitarle nada a nadie. El fondo comunitario se agota a la vista de todos.',
    testId: 'feature-social',
  },
  {
    icon: Calendar, color: '#B026FF',
    title: 'Organizador de Eventos',
    desc: 'Programa fiestas AURA en tu ciudad, registra contendientes por QR y declara ganadores en tiempo real.',
    testId: 'feature-events',
  },
  {
    icon: ScanLine, color: '#00F0FF',
    title: 'QR personal',
    desc: 'Un código único te identifica en cualquier evento del planeta y activa retos instantáneos.',
    testId: 'feature-qr',
  },
  {
    icon: Trophy, color: '#FFD166',
    title: 'Rankings & Niveles',
    desc: 'De NPC a AURA GOD. Sube de rango con victorias, buenas acciones y momentos que la comunidad reconozca.',
    testId: 'feature-ranking',
  },
];

const MILESTONES = [
  { k: 'Regístrate', v: 'Recibe 10,000 puntos y tu QR personal' },
  { k: 'Farmea', v: 'Sube momentos, gana batallas, reconoce acciones' },
  { k: 'Vive Aura', v: 'Encuentra eventos en tu ciudad, súmate al movimiento' },
];

const CITIES = [
  { name: 'CDMX', country: 'MX' }, { name: 'Guadalajara', country: 'MX' },
  { name: 'Monterrey', country: 'MX' }, { name: 'Bogotá', country: 'CO' },
  { name: 'Lima', country: 'PE' }, { name: 'Buenos Aires', country: 'AR' },
  { name: 'Madrid', country: 'ES' }, { name: 'Miami', country: 'US' },
  { name: 'São Paulo', country: 'BR' }, { name: 'Santiago', country: 'CL' },
];

function StatPill({ label, value, testId, tone = 'cyan' }) {
  const v = useCountUp(value || 0);
  return (
    <div data-testid={testId} className="aura-card p-4 relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 h-16 w-16 rounded-full blur-2xl ${tone === 'cyan' ? 'bg-aura-cyan/30' : tone === 'pink' ? 'bg-aura-pink/30' : 'bg-aura-violet/30'}`} />
      <div className="relative">
        <div className="font-mono font-black text-white text-2xl md:text-3xl tracking-tight">
          {v.toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-aura-textDim mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // If already logged in, redirect to /home
    if (user) nav('/home', { replace: true });
  }, [user, nav]);

  // Capture ?ref=<username> and persist for the signup flow
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const ref = p.get('ref');
      if (ref) localStorage.setItem('aura.ref', ref.trim().replace(/^@/, '').slice(0, 32));
    } catch (e) { console.warn('Ref capture failed', e); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/public/stats');
        setStats(data);
      } catch (e) { console.warn('Failed to load public stats', e); }
    })();
  }, []);

  const cities = useMemo(() => {
    const base = CITIES.slice();
    // Duplicate for marquee smoothness
    return [...base, ...base];
  }, []);

  return (
    <div className="min-h-screen bg-aura-bg relative overflow-x-hidden text-white">
      {/* Ambient background layers */}
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-40 -left-24 h-[420px] w-[420px] rounded-full bg-aura-cyan/25 blur-3xl animate-aura-pulse" />
      <div className="absolute top-1/3 -right-24 h-[520px] w-[520px] rounded-full bg-aura-violet/25 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[360px] w-[360px] rounded-full bg-aura-pink/20 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '3px 3px' }} />

      {/* ============ NAV ============ */}
      <nav className="relative z-20 flex items-center justify-between px-5 md:px-10 pt-6" data-testid="landing-nav">
        <Link to="/" className="flex items-center gap-2">
          <div className="grid place-items-center h-9 w-9 rounded-xl bg-aura-gradient shadow-neon-cyan">
            <Zap className="text-black" strokeWidth={2.8} size={18} />
          </div>
          <span className="font-display font-black text-lg tracking-[0.4em] aura-text-gradient">AURA</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link to="/auth?mode=signin" data-testid="nav-signin" className="hidden sm:inline-flex text-xs font-display font-semibold text-white/80 hover:text-white transition tracking-widest">INGRESAR</Link>
          <Link to="/plans" data-testid="nav-signup" className="aura-btn-primary !text-xs !py-2">
            <Zap size={14} /> ÚNETE
          </Link>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <section className="relative z-10 px-5 md:px-10 pt-10 md:pt-16 pb-16 max-w-7xl mx-auto" data-testid="hero">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          {/* Left column */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-aura-pink opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-aura-pink" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/70 font-display font-semibold">
                Movimiento mundial · {stats?.live_battles ?? '—'} batallas en vivo
              </span>
            </div>

            <h1 className="font-display font-black leading-[0.9] tracking-tight text-white text-5xl sm:text-6xl md:text-7xl lg:text-[92px]">
              <span className="block">Farmea tu</span>
              <span className="block aura-text-gradient drop-shadow-[0_0_28px_rgba(0,240,255,0.35)]">AURA.</span>
              <span className="block text-white/70 text-3xl sm:text-4xl md:text-5xl mt-3 font-black">
                Se recompensa. Se apuesta. Se comparte.
              </span>
            </h1>

            <p className="mt-6 text-aura-textDim text-base md:text-lg max-w-xl">
              Aura es el <span className="text-white">movimiento social gamificado</span> donde tu energía se convierte en puntos. Reta contrincantes con tu QR, gana en batallas votadas por la gente, reconoce buenas acciones desde el <span className="text-aura-gold">pool social de 1,000,000</span> y sube de rango hasta ser <span className="aura-text-gradient font-black">AURA GOD</span>.
            </p>

            {/* Primary CTAs */}
            <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-lg">
              <Link to="/auth" data-testid="cta-signup-user" className="aura-btn-primary !py-4 !text-base justify-center group">
                <Zap size={18} /> Regístrate y farmea
                <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition" />
              </Link>
              <Link to="/auth?role=organizer" data-testid="cta-signup-organizer" className="aura-btn-ghost !py-4 !text-base justify-center border-aura-gold/40 hover:border-aura-gold group">
                <Sparkles size={18} className="text-aura-gold" /> Soy Organizador
                <ArrowRight size={16} className="ml-1 text-aura-gold group-hover:translate-x-1 transition" />
              </Link>
            </div>

            <div className="mt-3 flex items-center gap-3 text-xs text-aura-textDim">
              <Link to="/auth?mode=signin" data-testid="cta-signin" className="hover:text-white transition inline-flex items-center gap-1">
                <Play size={12} /> Ya tengo cuenta
              </Link>
              <span className="text-white/20">·</span>
              <Link to="/auth?mode=signin&admin=1" data-testid="cta-admin" className="hover:text-white transition inline-flex items-center gap-1">
                <ShieldCheck size={12} /> Acceso Admin
              </Link>
            </div>

            {/* Milestones micro-strip */}
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-2xl">
              {MILESTONES.map((m, i) => (
                <div key={m.k} className="aura-card p-3">
                  <div className="text-[9px] tracking-[0.3em] font-display font-black text-aura-cyan">STEP {i + 1}</div>
                  <div className="text-sm font-display font-bold mt-1">{m.k}</div>
                  <div className="text-[11px] text-aura-textDim mt-0.5">{m.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — animated hero card */}
          <div className="relative">
            <HeroOrb stats={stats} />
          </div>
        </div>
      </section>

      {/* ============ QUIENES SOMOS (LEGAL) ============ */}
      <section className="relative z-10 px-5 md:px-10 py-8 max-w-5xl mx-auto" data-testid="about-legal">
        <div className="aura-card p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-aura-cyan/15 blur-3xl" />
          <div className="relative grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
            <div className="grid place-items-center h-14 w-14 rounded-2xl bg-aura-gradient text-black shadow-neon-cyan">
              <Zap size={26} strokeWidth={2.6} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.35em] font-display font-black text-aura-cyan">
                Quiénes Somos
              </div>
              <div className="font-display font-black text-white text-lg md:text-xl mt-1">
                Marketing de Inteligencia Artificial S. de R.L. de C.V.
              </div>
              <div className="text-xs md:text-sm text-aura-textDim mt-2 leading-relaxed">
                Av. Américas 1545, pisos 21, 22 y 23, Colonia Providencia,<br className="hidden md:block" />
                Guadalajara, Jalisco, México
              </div>
            </div>
            <div className="flex md:flex-col gap-2 shrink-0">
              <Link to="/terminos" data-testid="link-terms" className="aura-btn-ghost !text-xs !py-2 justify-center">
                Términos de Servicio
              </Link>
              <Link to="/privacidad" data-testid="link-privacy" className="aura-btn-ghost !text-xs !py-2 justify-center">
                Aviso de Privacidad
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ GLOBAL PULSE STATS ============ */}
      <section className="relative z-10 px-5 md:px-10 pb-16 max-w-7xl mx-auto" data-testid="global-stats">
        <div className="flex items-center gap-2 mb-4">
          <Radio size={14} className="text-aura-pink animate-aura-pulse" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-pink">Pulso global en vivo</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill testId="stat-users" label="Aura Farmers" value={stats?.total_users} tone="cyan" />
          <StatPill testId="stat-battles" label="Batallas totales" value={stats?.total_battles} tone="pink" />
          <StatPill testId="stat-cities" label="Ciudades activas" value={stats?.unique_cities} tone="violet" />
          <StatPill testId="stat-events" label="Eventos AURA" value={stats?.total_events} tone="cyan" />
          <StatPill testId="stat-pool" label="Pool social restante" value={stats?.social_pool_balance} tone="violet" />
          <StatPill testId="stat-battles-24h" label="Batallas 24h" value={stats?.battles_24h} tone="pink" />
          <StatPill testId="stat-aura-circulation" label="Aura en circulación" value={stats?.total_aura_in_circulation} tone="cyan" />
          <StatPill testId="stat-live" label="Batallas en vivo" value={stats?.live_battles} tone="pink" />
        </div>
      </section>

      {/* ============ CITIES MARQUEE ============ */}
      <section className="relative z-10 border-y border-white/5 bg-white/[0.02] py-4 overflow-hidden" data-testid="cities-marquee">
        <div className="flex items-center gap-8 animate-[marquee_35s_linear_infinite]" style={{ width: 'max-content' }}>
          {cities.map((c, i) => (
            <div key={`${c.name}-${i}`} className="flex items-center gap-2 text-white/60">
              <Globe2 size={14} className="text-aura-cyan" />
              <span className="font-display font-bold tracking-widest text-sm">{c.name.toUpperCase()}</span>
              <span className="font-mono text-[10px] text-aura-textDim">· {c.country}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ============ WHAT AURA OFFERS ============ */}
      <section className="relative z-10 px-5 md:px-10 py-20 max-w-7xl mx-auto" data-testid="features">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.35em] font-display font-black text-aura-cyan">Todo lo que AURA hace</div>
          <h2 className="mt-3 font-display font-black text-4xl md:text-6xl leading-[0.95]">
            La <span className="aura-text-gradient">plataforma</span> completa<br />
            de tu energía social.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={f.testId} className="aura-card p-6 relative overflow-hidden group hover:border-white/20 transition">
              <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full blur-2xl opacity-40 group-hover:opacity-80 transition"
                style={{ backgroundColor: f.color }} />
              <div className="relative">
                <div className="h-11 w-11 rounded-xl border grid place-items-center mb-4"
                  style={{ borderColor: `${f.color}55`, backgroundColor: `${f.color}15` }}>
                  <f.icon size={20} style={{ color: f.color }} />
                </div>
                <h3 className="font-display font-black text-white text-xl">{f.title}</h3>
                <p className="text-sm text-aura-textDim mt-2">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ HOW THE POINTS WORK ============ */}
      <section className="relative z-10 px-5 md:px-10 py-20 max-w-7xl mx-auto" data-testid="how-points">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] font-display font-black text-aura-pink">Sistema de puntos</div>
            <h2 className="mt-3 font-display font-black text-4xl md:text-5xl leading-[0.95]">
              Puntos que <span className="aura-text-gradient">se ganan</span>,<br />no se regalan.
            </h2>
            <ul className="mt-6 space-y-3 text-sm md:text-base text-white/80">
              <li className="flex items-start gap-3"><Wallet size={18} className="text-aura-cyan mt-0.5" /> <span>Tu <b className="text-white">billetera</b> arranca con <b className="text-aura-cyan">10,000 puntos</b> al registrarte.</span></li>
              <li className="flex items-start gap-3"><Swords size={18} className="text-aura-pink mt-0.5" /> <span>Apuestas puntos en cada <b className="text-white">Batalla Aura</b>. El ganador se lleva el pot completo.</span></li>
              <li className="flex items-start gap-3"><HeartHandshake size={18} className="text-aura-gold mt-0.5" /> <span>Suma puntos cuando la comunidad te reconozca una <b className="text-white">buena acción</b> (máx <b className="text-aura-gold">100 por vez</b>).</span></li>
              <li className="flex items-start gap-3"><ShieldCheck size={18} className="text-aura-violet mt-0.5" /> <span>Los <b className="text-white">Puntos Sociales</b> nunca salen del bolsillo de otro usuario. Vienen del <b className="text-aura-violet">pool comunitario de 1,000,000</b>.</span></li>
              <li className="flex items-start gap-3"><TrendingUp size={18} className="text-aura-cyan mt-0.5" /> <span>Todo movimiento queda en un <b className="text-white">ledger inmutable</b>. Cero magia, todo auditable.</span></li>
            </ul>
          </div>

          <div className="relative">
            <div className="aura-card p-6 md:p-8 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-aura-gold/20 blur-3xl" />
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-aura-textDim font-display font-black">Pool de Puntos Sociales</div>
                  <Sparkles size={16} className="text-aura-gold" />
                </div>
                <div className="font-mono font-black text-white text-4xl md:text-5xl tracking-tight">
                  {(stats?.social_pool_balance ?? 1_000_000).toLocaleString()}
                </div>
                <div className="text-[11px] text-aura-textDim mt-1">
                  de {(stats?.social_pool_initial ?? 1_000_000).toLocaleString()} iniciales · máx 100 por reconocimiento
                </div>

                <div className="mt-4 h-3 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-aura-cyan via-aura-violet to-aura-pink"
                    style={{ width: `${Math.max(0, Math.min(100, ((stats?.social_pool_balance ?? 1_000_000) / Math.max(stats?.social_pool_initial || 1, 1)) * 100))}%` }}
                  />
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="border border-white/10 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest text-aura-textDim">Reconocimientos</div>
                    <div className="font-mono font-bold text-white text-xl mt-1">{(stats?.approved_recognitions || 0).toLocaleString()}</div>
                  </div>
                  <div className="border border-white/10 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest text-aura-textDim">Aura circulando</div>
                    <div className="font-mono font-bold text-white text-xl mt-1">{(stats?.total_aura_in_circulation || 0).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ROLES / SIGNUP DOORS ============ */}
      <section className="relative z-10 px-5 md:px-10 py-20 max-w-7xl mx-auto" data-testid="roles-doors">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.35em] font-display font-black text-aura-violet">Elige tu puerta</div>
          <h2 className="mt-3 font-display font-black text-4xl md:text-5xl leading-[0.95]">
            ¿Cómo entras al <span className="aura-text-gradient">movimiento</span>?
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <RoleCard
            testId="role-user"
            to="/auth"
            color="#00F0FF"
            icon={<Users size={22} />}
            eyebrow="Aura Farmer"
            title="Regístrate como usuario"
            desc="Obtén 10,000 puntos, tu QR personal y súmate a las batallas de tu ciudad."
            cta="Crear mi cuenta"
          />
          <RoleCard
            testId="role-organizer"
            to="/auth?role=organizer"
            color="#FFD166"
            icon={<Sparkles size={22} />}
            eyebrow="Organizador"
            title="Organiza eventos AURA"
            desc="Panel de organizador, registro por QR de contendientes, calendario con IDs progresivos."
            cta="Registrar organizador"
          />
          <RoleCard
            testId="role-admin"
            to="/auth?mode=signin&admin=1"
            color="#B026FF"
            icon={<Crown size={22} />}
            eyebrow="Admin"
            title="Acceso Administrador"
            desc="Métricas globales, moderación, reversiones y recarga del pool social."
            cta="Ingresar al panel"
          />
        </div>
      </section>

      {/* ============ TOP AURA HOLDERS ============ */}
      {stats?.top_aura?.length > 0 && (
        <section className="relative z-10 px-5 md:px-10 py-20 max-w-7xl mx-auto" data-testid="top-holders">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] font-display font-black text-aura-cyan">Top Aura Global</div>
              <h2 className="mt-2 font-display font-black text-3xl md:text-4xl">
                Los <span className="aura-text-gradient">3</span> con más energía ahora
              </h2>
            </div>
            <Trophy size={30} className="text-aura-gold hidden md:block" />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {stats.top_aura.map((u, i) => (
              <div key={u.user_id} data-testid={`top-user-${i + 1}`} className="aura-card p-5 flex items-center gap-4">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-aura-gradient text-black font-display font-black text-lg">
                  {i + 1}
                </div>
                <UserAvatar user={u} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-black text-white text-lg truncate">{u.name}</div>
                  <div className="text-xs text-aura-textDim">@{u.username}</div>
                  <div className="text-[11px] text-aura-textDim mt-0.5 flex items-center gap-1"><MapPin size={10} /> {u.city}, {u.country}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-black text-aura-cyan text-lg">{(u.aura_points || 0).toLocaleString()}</div>
                  <div className="text-[9px] uppercase tracking-widest text-aura-textDim">AURA</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ============ FINAL CTA ============ */}
      <section className="relative z-10 px-5 md:px-10 py-24 max-w-7xl mx-auto text-center" data-testid="final-cta">
        <div className="aura-card p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-32 -left-24 h-80 w-80 rounded-full bg-aura-cyan/25 blur-3xl" />
          <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-aura-violet/30 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.35em] font-display font-black text-aura-cyan">Es tu momento</div>
            <h2 className="mt-3 font-display font-black text-4xl md:text-6xl leading-[0.9]">
              Este movimiento<br />
              <span className="aura-text-gradient">apenas empieza</span>.
            </h2>
            <p className="mt-4 text-aura-textDim max-w-xl mx-auto">
              Únete a los primeros Aura Farmers que están cambiando la forma en que se recompensa la energía social en el mundo.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/auth" data-testid="final-cta-signup" className="aura-btn-primary !py-4 !text-base">
                <Zap size={18} /> Empezar a farmear
              </Link>
              <Link to="/events" data-testid="final-cta-events" className="aura-btn-ghost !py-4 !text-base">
                <Calendar size={18} /> Ver calendario mundial
              </Link>
              <Link to="/vote" data-testid="final-cta-vote" className="aura-btn-ghost !py-4 !text-base border-aura-pink/40 hover:border-aura-pink">
                <Swords size={18} className="text-aura-pink" /> Votar en un evento
              </Link>
              <Link to="/plans" data-testid="final-cta-plans" className="aura-btn-ghost !py-4 !text-base border-aura-gold/40 hover:border-aura-gold">
                <Crown size={18} className="text-aura-gold" /> Ver paquetes ELITE
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative z-10 px-5 md:px-10 py-10 border-t border-white/5 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Zap size={16} className="text-aura-cyan" />
          <span className="font-display font-black tracking-[0.4em] text-white/70 text-sm">AURA</span>
        </div>
        <div className="text-xs text-aura-textDim">EL AURA SE GANA, NO SE REGALA</div>
        <div className="mt-4 flex items-center justify-center gap-4 text-xs">
          <Link to="/terminos" className="text-white/60 hover:text-white transition">Términos de Servicio</Link>
          <span className="text-white/20">·</span>
          <Link to="/privacidad" className="text-white/60 hover:text-white transition">Aviso de Privacidad</Link>
        </div>
        <div className="mt-4 text-[10px] text-aura-textDim leading-relaxed max-w-xl mx-auto">
          © 2026 Marketing de Inteligencia Artificial S. de R.L. de C.V.<br />
          Av. Américas 1545, pisos 21, 22 y 23, Colonia Providencia, Guadalajara, Jalisco, México
        </div>
      </footer>

      {/* Local keyframes for marquee */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

function RoleCard({ testId, to, color, icon, eyebrow, title, desc, cta }) {
  return (
    <Link to={to} data-testid={testId} className="aura-card p-6 relative overflow-hidden group hover:border-white/25 transition block">
      <div className="absolute -top-16 -right-16 h-36 w-36 rounded-full blur-3xl opacity-40 group-hover:opacity-80 transition" style={{ backgroundColor: color }} />
      <div className="relative">
        <div className="h-12 w-12 rounded-2xl border grid place-items-center mb-4"
          style={{ borderColor: `${color}55`, backgroundColor: `${color}18`, color }}>
          {icon}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black" style={{ color }}>{eyebrow}</div>
        <h3 className="font-display font-black text-white text-2xl mt-2">{title}</h3>
        <p className="text-sm text-aura-textDim mt-2 min-h-[3rem]">{desc}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-sm font-display font-bold text-white group-hover:gap-3 transition">
          {cta} <ArrowRight size={16} />
        </div>
      </div>
    </Link>
  );
}

function HeroOrb({ stats }) {
  return (
    <div className="relative aspect-square max-w-md mx-auto lg:max-w-none">
      {/* Rotating rings */}
      <div className="absolute inset-0 rounded-full border border-aura-cyan/20 animate-[spin_28s_linear_infinite]" />
      <div className="absolute inset-6 rounded-full border border-aura-violet/25 animate-[spin_18s_linear_infinite_reverse]" />
      <div className="absolute inset-12 rounded-full border border-aura-pink/25 animate-[spin_36s_linear_infinite]" />

      {/* Core orb */}
      <div className="absolute inset-16 rounded-full bg-aura-gradient shadow-[0_0_120px_rgba(0,240,255,0.35)] flex items-center justify-center">
        <div className="absolute inset-3 rounded-full bg-aura-bg" />
        <div className="relative text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-cyan mb-1">AURA en vivo</div>
          <div className="font-mono font-black text-white text-3xl md:text-4xl">
            {(stats?.total_aura_in_circulation ?? 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-aura-textDim mt-1 max-w-[10rem] mx-auto">
            puntos circulando ahora mismo
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-display font-black text-aura-pink">
            <Radio size={10} className="animate-aura-pulse" /> LIVE
          </div>
        </div>
      </div>

      {/* Floating badges */}
      <FloatingBadge className="absolute top-4 -left-3 md:-left-8" color="#00F0FF" icon={<Swords size={14} />} label={`${stats?.live_battles ?? 0} en vivo`} />
      <FloatingBadge className="absolute bottom-6 -right-2 md:-right-8" color="#FFD166" icon={<HeartHandshake size={14} />} label="Pool 1M" />
      <FloatingBadge className="absolute top-1/2 -right-2 md:-right-10 -translate-y-1/2" color="#B026FF" icon={<Calendar size={14} />} label={`${stats?.upcoming_events ?? 0} eventos`} />
      <FloatingBadge className="absolute bottom-4 left-0 md:-left-6" color="#FF2E8B" icon={<ScanLine size={14} />} label="QR AURA" />
    </div>
  );
}

function FloatingBadge({ className = '', color, icon, label }) {
  return (
    <div className={`${className} bg-aura-surface/85 backdrop-blur-xl border rounded-full px-3 py-1.5 flex items-center gap-2 shadow-lg`}
      style={{ borderColor: `${color}55`, boxShadow: `0 0 22px ${color}44` }}>
      <span style={{ color }}>{icon}</span>
      <span className="text-[11px] font-display font-bold tracking-wider text-white">{label}</span>
    </div>
  );
}
